/**
 * Custom Roles Domain Seed Data
 *
 * Contains all seed data for Custom Roles and Permissions:
 * - Custom Roles (business-defined roles beyond defaults)
 * - Permission Sets (grouped permissions for roles)
 * - Role Assignments
 *
 * Custom roles allow businesses to define their own permission sets
 * beyond the default platform roles (admin, user, viewer).
 *
 * @see KB Article: kb-business-custom-roles
 * @see Issue #293: Enterprise Permission System
 */

import type { PermissionValue } from '../../enums/permissions';
import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// CUSTOM ROLE IDs
// =============================================================================

export const CUSTOM_ROLE_IDS = {
  // OpsMatrix Capital Custom Roles
  BUSINESS_ADMINISTRATOR: seedId('role-business-admin', 1),
  SENIOR_UNDERWRITER: seedId('role-senior-uw', 1),
  JUNIOR_UNDERWRITER: seedId('role-junior-uw', 1),
  LOAN_CLOSER: seedId('role-closer', 1),
  DRAW_COORDINATOR: seedId('role-draw-coord', 1),
  COMPLIANCE_OFFICER: seedId('role-compliance', 1),
  PORTFOLIO_ANALYST: seedId('role-portfolio', 1),

  // Standard Industry Roles (Issue #469: Seed Data Gap Analysis)
  LOAN_OFFICER: seedId('role-loan-officer', 1),
  PROCESSOR: seedId('role-processor', 1),
  VIEWER: seedId('role-viewer', 1),

  // 1Sharpe Capital Custom Roles
  SHARPE_INVESTMENT_ANALYST: seedId('role-sharpe-analyst', 1),
  SHARPE_CONSTRUCTION_MANAGER: seedId('role-sharpe-constr', 1),

  // Pacific Bridge Fund Custom Roles
  PACIFIC_DEAL_SOURCER: seedId('role-pacific-sourcer', 1),
  PACIFIC_FUND_ACCOUNTANT: seedId('role-pacific-acct', 1),
} as const;

export type CustomRoleId = (typeof CUSTOM_ROLE_IDS)[keyof typeof CUSTOM_ROLE_IDS];

// =============================================================================
// PERMISSION CATEGORIES
// =============================================================================

export const PERMISSION_CATEGORIES = {
  CASES: 'cases',
  CONSUMERS: 'consumers',
  DOCUMENTS: 'documents',
  PIPELINE: 'pipeline',
  TEAMS: 'teams',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  BILLING: 'billing',
  ADMIN: 'admin',
} as const;

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[keyof typeof PERMISSION_CATEGORIES];

// =============================================================================
// PERMISSIONS
// =============================================================================

export const SEED_PERMISSIONS = {
  // Case Permissions
  CASE_VIEW: 'case:view',
  CASE_CREATE: 'case:create',
  CASE_EDIT: 'case:edit',
  CASE_DELETE: 'case:delete',
  CASE_ASSIGN: 'case:assign',
  CASE_TRANSITION: 'case:transition',
  CASE_APPROVE: 'case:approve',
  CASE_FUND: 'case:fund',

  // Consumer Permissions
  CONSUMER_VIEW: 'consumer:view',
  CONSUMER_VIEW_PII: 'consumer:view_pii',
  CONSUMER_CREATE: 'consumer:create',
  CONSUMER_EDIT: 'consumer:edit',
  CONSUMER_DELETE: 'consumer:delete',
  CONSUMER_CREDIT_PULL: 'consumer:credit_pull',

  // Document Permissions
  DOCUMENT_VIEW: 'document:view',
  DOCUMENT_UPLOAD: 'document:upload',
  DOCUMENT_DOWNLOAD: 'document:download',
  DOCUMENT_DELETE: 'document:delete',
  DOCUMENT_SENSITIVE: 'document:sensitive',

  // Pipeline Permissions
  PIPELINE_VIEW: 'pipeline:view',
  PIPELINE_CONFIGURE: 'pipeline:configure',
  PIPELINE_OVERRIDE_SLA: 'pipeline:override_sla',
  PIPELINE_APPROVE_EXCEPTION: 'pipeline:approve_exception',

  // Team Permissions
  TEAM_VIEW: 'team:view',
  TEAM_MANAGE: 'team:manage',
  TEAM_ASSIGN: 'team:assign',

  // Report Permissions
  REPORT_VIEW: 'report:view',
  REPORT_EXPORT: 'report:export',
  REPORT_CONFIGURE: 'report:configure',

  // Settings Permissions
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_CONFIGURE: 'settings:configure',
  SETTINGS_INTEGRATIONS: 'settings:integrations',

  // Billing Permissions
  BILLING_VIEW: 'billing:view',
  BILLING_MANAGE: 'billing:manage',

  // Admin Permissions
  ADMIN_USERS: 'admin:users',
  ADMIN_ROLES: 'admin:roles',
  ADMIN_AUDIT: 'admin:audit',
} as const;

export type SeedPermission = (typeof SEED_PERMISSIONS)[keyof typeof SEED_PERMISSIONS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface CustomRoleSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  /**
   * Permissions can include:
   * - SeedPermission strings (e.g., 'case:view', 'document:upload')
   * - PermissionValue strings (e.g., 'users:read', 'agents:read')
   * - Wildcard '*' for full access
   */
  permissions: (SeedPermission | PermissionValue | '*')[];
  inheritsFrom?: string; // Base role to inherit from
  maxUsers?: number; // Optional limit on role assignments
  isDefault: boolean;
  isActive: boolean;
  createdBy: string;
}

export interface RoleAssignmentSeedData {
  id: string;
  businessId: string;
  userId: string;
  roleId: string;
  assignedBy: string;
  assignedAt: string;
  expiresAt?: string;
  scope?: {
    type: 'all' | 'team' | 'territory' | 'product';
    values?: string[];
  };
}

// =============================================================================
// CUSTOM ROLE DATA
// =============================================================================

export const CUSTOM_ROLES: Record<keyof typeof CUSTOM_ROLE_IDS, CustomRoleSeedData> = {
  // ==========================================================================
  // OPSMATRIX CAPITAL CUSTOM ROLES
  // ==========================================================================
  BUSINESS_ADMINISTRATOR: {
    id: CUSTOM_ROLE_IDS.BUSINESS_ADMINISTRATOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Business Administrator',
    description: 'Full administrative access to all business functions',
    color: '#DC2626', // Red - admin indicator
    icon: 'shield-check',
    permissions: [
      // Wildcard - full access to everything
      '*',
      // Explicit permissions for frontend navigation (Permission enum format)
      // Users/Team management
      'users:read',
      'users:create',
      'users:update',
      'users:delete',
      // Agents/AI
      'agents:read',
      'agents:create',
      'agents:update',
      'agents:delete',
      'agents:deploy',
      'agents:configure',
      // Documents
      'documents:read',
      'documents:write',
      'documents:upload',
      'documents:delete',
      'documents:analyze',
      'documents:sensitive_read',
      'documents:restricted_read',
      // Settings
      'settings:read',
      'settings:update',
      'settings:webhooks:create',
      'settings:webhooks:update',
      'settings:webhooks:delete',
      'settings:pipelines:create',
      'settings:pipelines:update',
      'settings:pipelines:delete',
      // Integrations
      'integrations:read',
      'integrations:create',
      'integrations:update',
      'integrations:delete',
      // Audit
      'audit:read',
      'audit:export',
      // Analytics
      'analytics:read',
      'analytics:export',
      // Billing
      'billing:read',
      'billing:update',
      // Templates
      'templates:read',
      'templates:create',
      // Consumer data
      'consumer:pii_access',
      'consumer:credit_pull',
      'consumer:financials_view',
      // Notifications
      'notifications:read',
      'notifications:create',
      'notifications:update',
      'notifications:delete',
      // Teams (Issue #428)
      'teams:manage_members',
      // Also include SEED_PERMISSIONS for backward compatibility
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_CREATE,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CASE_DELETE,
      SEED_PERMISSIONS.CASE_ASSIGN,
      SEED_PERMISSIONS.CASE_TRANSITION,
      SEED_PERMISSIONS.CASE_APPROVE,
      SEED_PERMISSIONS.CASE_FUND,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW_PII,
      SEED_PERMISSIONS.CONSUMER_CREATE,
      SEED_PERMISSIONS.CONSUMER_EDIT,
      SEED_PERMISSIONS.CONSUMER_DELETE,
      SEED_PERMISSIONS.CONSUMER_CREDIT_PULL,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.DOCUMENT_DELETE,
      SEED_PERMISSIONS.DOCUMENT_SENSITIVE,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.PIPELINE_CONFIGURE,
      SEED_PERMISSIONS.PIPELINE_OVERRIDE_SLA,
      SEED_PERMISSIONS.PIPELINE_APPROVE_EXCEPTION,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.TEAM_MANAGE,
      SEED_PERMISSIONS.TEAM_ASSIGN,
      SEED_PERMISSIONS.REPORT_VIEW,
      SEED_PERMISSIONS.REPORT_EXPORT,
      SEED_PERMISSIONS.REPORT_CONFIGURE,
      SEED_PERMISSIONS.SETTINGS_VIEW,
      SEED_PERMISSIONS.SETTINGS_CONFIGURE,
      SEED_PERMISSIONS.SETTINGS_INTEGRATIONS,
      SEED_PERMISSIONS.BILLING_VIEW,
      SEED_PERMISSIONS.BILLING_MANAGE,
      SEED_PERMISSIONS.ADMIN_USERS,
      SEED_PERMISSIONS.ADMIN_ROLES,
      SEED_PERMISSIONS.ADMIN_AUDIT,
    ],
    inheritsFrom: 'business_admin',
    isDefault: true, // Default role for business admins
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  SENIOR_UNDERWRITER: {
    id: CUSTOM_ROLE_IDS.SENIOR_UNDERWRITER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Senior Underwriter',
    description: 'Experienced underwriter with approval authority for complex loans',
    color: '#8B5CF6', // Purple
    icon: 'shield-check',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CASE_ASSIGN,
      SEED_PERMISSIONS.CASE_TRANSITION,
      SEED_PERMISSIONS.CASE_APPROVE,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW_PII,
      SEED_PERMISSIONS.CONSUMER_CREDIT_PULL,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.DOCUMENT_SENSITIVE,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.PIPELINE_APPROVE_EXCEPTION,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
      SEED_PERMISSIONS.REPORT_EXPORT,
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  JUNIOR_UNDERWRITER: {
    id: CUSTOM_ROLE_IDS.JUNIOR_UNDERWRITER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Junior Underwriter',
    description: 'Entry-level underwriter with limited approval authority',
    color: '#06B6D4', // Cyan
    icon: 'academic-cap',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW_PII,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  LOAN_CLOSER: {
    id: CUSTOM_ROLE_IDS.LOAN_CLOSER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Closer',
    description: 'Handles loan closing and funding processes',
    color: '#10B981', // Green
    icon: 'banknotes',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CASE_TRANSITION,
      SEED_PERMISSIONS.CASE_FUND,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW_PII,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.DOCUMENT_SENSITIVE,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  DRAW_COORDINATOR: {
    id: CUSTOM_ROLE_IDS.DRAW_COORDINATOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Draw Coordinator',
    description: 'Manages construction draw requests and inspections',
    color: '#F59E0B', // Amber
    icon: 'wrench',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CASE_TRANSITION,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  COMPLIANCE_OFFICER: {
    id: CUSTOM_ROLE_IDS.COMPLIANCE_OFFICER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Compliance Officer',
    description: 'Oversees regulatory compliance and audit functions',
    color: '#EF4444', // Red
    icon: 'shield-exclamation',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW_PII,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.DOCUMENT_SENSITIVE,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.PIPELINE_CONFIGURE,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
      SEED_PERMISSIONS.REPORT_EXPORT,
      SEED_PERMISSIONS.REPORT_CONFIGURE,
      SEED_PERMISSIONS.SETTINGS_VIEW,
      SEED_PERMISSIONS.ADMIN_AUDIT,
    ],
    inheritsFrom: 'business_admin',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  PORTFOLIO_ANALYST: {
    id: CUSTOM_ROLE_IDS.PORTFOLIO_ANALYST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Portfolio Analyst',
    description: 'Analyzes loan portfolio performance and risk',
    color: '#3B82F6', // Blue
    icon: 'chart-bar',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
      SEED_PERMISSIONS.REPORT_EXPORT,
      SEED_PERMISSIONS.REPORT_CONFIGURE,
    ],
    inheritsFrom: 'business_viewer',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  // ==========================================================================
  // STANDARD INDUSTRY ROLES (Issue #469)
  // ==========================================================================
  LOAN_OFFICER: {
    id: CUSTOM_ROLE_IDS.LOAN_OFFICER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Officer',
    description: 'Originates loans and manages borrower relationships',
    color: '#0EA5E9', // Sky blue
    icon: 'user-plus',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_CREATE,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CASE_ASSIGN,
      SEED_PERMISSIONS.CASE_TRANSITION,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW_PII,
      SEED_PERMISSIONS.CONSUMER_CREATE,
      SEED_PERMISSIONS.CONSUMER_EDIT,
      SEED_PERMISSIONS.CONSUMER_CREDIT_PULL,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
      // Permission enum format
      'consumer:pii_access',
      'consumer:credit_pull',
      'documents:read',
      'documents:upload',
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  PROCESSOR: {
    id: CUSTOM_ROLE_IDS.PROCESSOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Processor',
    description: 'Processes loan applications and manages documentation',
    color: '#A855F7', // Purple
    icon: 'document-text',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CASE_TRANSITION,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW_PII,
      SEED_PERMISSIONS.CONSUMER_EDIT,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
      // Permission enum format
      'consumer:pii_access',
      'documents:read',
      'documents:upload',
      'documents:write',
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },
  VIEWER: {
    id: CUSTOM_ROLE_IDS.VIEWER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Viewer',
    description: 'Read-only access to view cases and reports',
    color: '#64748B', // Slate
    icon: 'eye',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
      // Permission enum format
      'documents:read',
      'analytics:read',
    ],
    inheritsFrom: 'business_viewer',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
  },

  // ==========================================================================
  // 1SHARPE CAPITAL CUSTOM ROLES
  // ==========================================================================
  SHARPE_INVESTMENT_ANALYST: {
    id: CUSTOM_ROLE_IDS.SHARPE_INVESTMENT_ANALYST,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Investment Analyst',
    description: 'Analyzes deals and prepares investment memos',
    color: '#6366F1', // Indigo
    icon: 'presentation-chart-bar',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_CREATE,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW_PII,
      SEED_PERMISSIONS.CONSUMER_CREATE,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
      SEED_PERMISSIONS.REPORT_EXPORT,
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.SHARPE_ADMIN,
  },
  SHARPE_CONSTRUCTION_MANAGER: {
    id: CUSTOM_ROLE_IDS.SHARPE_CONSTRUCTION_MANAGER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Construction Manager',
    description: 'Manages construction loans and draw processes',
    color: '#F97316', // Orange
    icon: 'building-office',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_EDIT,
      SEED_PERMISSIONS.CASE_TRANSITION,
      SEED_PERMISSIONS.CASE_APPROVE,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.PIPELINE_APPROVE_EXCEPTION,
      SEED_PERMISSIONS.TEAM_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.SHARPE_ADMIN,
  },

  // ==========================================================================
  // PACIFIC BRIDGE FUND CUSTOM ROLES
  // ==========================================================================
  PACIFIC_DEAL_SOURCER: {
    id: CUSTOM_ROLE_IDS.PACIFIC_DEAL_SOURCER,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Deal Sourcer',
    description: 'Sources and screens new investment opportunities',
    color: '#EC4899', // Pink
    icon: 'magnifying-glass',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CASE_CREATE,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.CONSUMER_CREATE,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_UPLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
    ],
    inheritsFrom: 'business_user',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.PACIFIC_ADMIN,
  },
  PACIFIC_FUND_ACCOUNTANT: {
    id: CUSTOM_ROLE_IDS.PACIFIC_FUND_ACCOUNTANT,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Fund Accountant',
    description: 'Manages fund accounting and investor reporting',
    color: '#14B8A6', // Teal
    icon: 'calculator',
    permissions: [
      SEED_PERMISSIONS.CASE_VIEW,
      SEED_PERMISSIONS.CONSUMER_VIEW,
      SEED_PERMISSIONS.DOCUMENT_VIEW,
      SEED_PERMISSIONS.DOCUMENT_DOWNLOAD,
      SEED_PERMISSIONS.PIPELINE_VIEW,
      SEED_PERMISSIONS.REPORT_VIEW,
      SEED_PERMISSIONS.REPORT_EXPORT,
      SEED_PERMISSIONS.REPORT_CONFIGURE,
      SEED_PERMISSIONS.BILLING_VIEW,
    ],
    inheritsFrom: 'business_viewer',
    isDefault: false,
    isActive: true,
    createdBy: USER_IDS.PACIFIC_ADMIN,
  },
};

// =============================================================================
// ROLE ASSIGNMENT DATA
// =============================================================================

export const ROLE_ASSIGNMENT_IDS = {
  RACHEL_ADMIN: seedId('role-assign-rachel-admin', 1),
  SARAH_SENIOR_UW: seedId('role-assign-sarah-uw', 1),
  ALEX_JUNIOR_UW: seedId('role-assign-alex-uw', 1),
  MIKE_J_CLOSER: seedId('role-assign-mike-closer', 1),
  JAMES_DRAW: seedId('role-assign-james-draw', 1),
  MARIA_COMPLIANCE: seedId('role-assign-maria-compliance', 1),
  ANALYST_SHARPE: seedId('role-assign-analyst-sharpe', 1),
} as const;

export type RoleAssignmentId = (typeof ROLE_ASSIGNMENT_IDS)[keyof typeof ROLE_ASSIGNMENT_IDS];

export const ROLE_ASSIGNMENTS: Record<keyof typeof ROLE_ASSIGNMENT_IDS, RoleAssignmentSeedData> = {
  RACHEL_ADMIN: {
    id: ROLE_ASSIGNMENT_IDS.RACHEL_ADMIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    roleId: CUSTOM_ROLE_IDS.BUSINESS_ADMINISTRATOR,
    assignedBy: USER_IDS.RACHEL_MARTINEZ, // Self-assigned as business owner
    assignedAt: '2023-01-01T09:00:00Z',
  },
  SARAH_SENIOR_UW: {
    id: ROLE_ASSIGNMENT_IDS.SARAH_SENIOR_UW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.SARAH_CHEN,
    roleId: CUSTOM_ROLE_IDS.SENIOR_UNDERWRITER,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    assignedAt: '2023-06-01T09:00:00Z',
  },
  ALEX_JUNIOR_UW: {
    id: ROLE_ASSIGNMENT_IDS.ALEX_JUNIOR_UW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.ALEX_KUMAR,
    roleId: CUSTOM_ROLE_IDS.JUNIOR_UNDERWRITER,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    assignedAt: '2024-02-01T09:00:00Z',
  },
  MIKE_J_CLOSER: {
    id: ROLE_ASSIGNMENT_IDS.MIKE_J_CLOSER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.MIKE_JOHNSON,
    roleId: CUSTOM_ROLE_IDS.LOAN_CLOSER,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    assignedAt: '2023-04-01T09:00:00Z',
  },
  JAMES_DRAW: {
    id: ROLE_ASSIGNMENT_IDS.JAMES_DRAW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.JAMES_WILSON,
    roleId: CUSTOM_ROLE_IDS.DRAW_COORDINATOR,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    assignedAt: '2023-05-01T09:00:00Z',
  },
  MARIA_COMPLIANCE: {
    id: ROLE_ASSIGNMENT_IDS.MARIA_COMPLIANCE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.MARIA_RODRIGUEZ,
    roleId: CUSTOM_ROLE_IDS.COMPLIANCE_OFFICER,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    assignedAt: '2023-06-01T09:00:00Z',
  },
  ANALYST_SHARPE: {
    id: ROLE_ASSIGNMENT_IDS.ANALYST_SHARPE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    userId: USER_IDS.ANALYST,
    roleId: CUSTOM_ROLE_IDS.SHARPE_INVESTMENT_ANALYST,
    assignedBy: USER_IDS.SHARPE_ADMIN,
    assignedAt: '2023-07-01T09:00:00Z',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllCustomRoles(workspace?: string): CustomRoleSeedData[] {
  const roles = Object.values(CUSTOM_ROLES);
  if (!workspace) return roles;
  return roles.map((r) => ({ ...r, name: `${r.name} [${workspace}]` }));
}

export function getAllRoleAssignments(): RoleAssignmentSeedData[] {
  return Object.values(ROLE_ASSIGNMENTS);
}

export function getRolesByBusiness(businessId: string): CustomRoleSeedData[] {
  return Object.values(CUSTOM_ROLES).filter((r) => r.businessId === businessId);
}

export function getAssignmentsByUser(userId: string): RoleAssignmentSeedData[] {
  return Object.values(ROLE_ASSIGNMENTS).filter((a) => a.userId === userId);
}

export function getAssignmentsByRole(roleId: string): RoleAssignmentSeedData[] {
  return Object.values(ROLE_ASSIGNMENTS).filter((a) => a.roleId === roleId);
}

export function getActiveRoles(): CustomRoleSeedData[] {
  return Object.values(CUSTOM_ROLES).filter((r) => r.isActive);
}

export function getRolesByPermission(permission: SeedPermission): CustomRoleSeedData[] {
  return Object.values(CUSTOM_ROLES).filter((r) => r.permissions.includes(permission));
}
